// People › a person › Live (D120, Console D views/people.js; APS-547): an internal tester's devices
// live, while the tab is open. Live logs (newest first, filtered by the Worker, paused here), the
// memory and battery of the last minute, the screen as a masked wireframe, and three device
// commands for owners and admins, each confirmed inline and audited by the API; their results come
// back over the view's socket only. The view explains every state the device or the Worker can be
// in: waiting for a device, paused or ended with the device's reason, the view's 30 minutes up, and
// live being unavailable. Everything the device sends is shown as text, never as markup.
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { isApiError } from '../api/client'
import { liveTicket, useLiveCommand } from '../api/queries'
import type { LiveTester } from '../api/types'
import { Btn, EmptyNote, Section, Table } from '../components/d'
import { LineChart } from '../components/LineChart'
import { Wireframe } from '../components/ReplayPlayer'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { shortId } from '../lib/format'
import {
  attributeText,
  COMMAND_TIMEOUT_MS,
  type CommandName,
  type CommandResult,
  diagnostics,
  type LiveDevice,
  type LiveLine,
  type LiveProblem,
  type LiveStatus,
  liveStatus,
  pickDevice,
  SEVERITY_FLOOR,
  type SeverityName,
  severityName,
} from '../lib/live'
import { keyframeScreen } from '../lib/replay'
import { useLiveView, waitUntil } from '../lib/useLiveView'

/** Whole seconds left until `at`, 0 once it passed. */
const waitS = (at: number | null, now: number) =>
  at === null ? 0 : Math.max(0, Math.ceil((at - now) / 1000))

/** The live Worker's origin this build may connect to; unset: no Live tab (env.d.ts). */
export const liveUrl = (): string => import.meta.env.VITE_LIVE_URL ?? ''

/** Lines the table shows; the view keeps LINES_KEPT. */
const LINES_SHOWN = 100
/** How long the filter waits for typing to stop before the Worker gets it. */
const FILTER_DEBOUNCE_MS = 300
/** The severity floors the filter offers. */
const LEVELS = ['all', 'debug', 'info', 'warn', 'error'] as const
type Level = (typeof LEVELS)[number]

const COMMAND_ROWS: { name: CommandName; title: MessageId; body: MessageId }[] = [
  {
    name: 'screenshot',
    title: 'people.live.cmd.screenshot',
    body: 'people.live.cmd.screenshotBody',
  },
  {
    name: 'diagnostics',
    title: 'people.live.cmd.diagnostics',
    body: 'people.live.cmd.diagnosticsBody',
  },
  { name: 'raise_log_level', title: 'people.live.cmd.raise', body: 'people.live.cmd.raiseBody' },
]

/** A command as this tab sent it; its outcome is the view's result with the same id. */
interface Sent {
  install: string
  at: number
  id?: string
  delivered?: boolean
  error?: 'not_live' | 'unavailable' | 'forbidden' | 'rate_limited' | 'failed'
}

/** A clock for the relative times and the commands' 2 minutes. */
function useNow(every: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), every)
    return () => clearInterval(id)
  }, [every])
  return now
}

export function LiveTab({
  projectId,
  tester,
  canEdit,
  liveUrl: origin = liveUrl(),
  onOnline,
}: {
  projectId: string
  tester: LiveTester
  canEdit: boolean | undefined
  liveUrl?: string
  /** Whether one of the tester's devices is online, for the Live tab's dot (the mock's). */
  onOnline?: (online: boolean) => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const ticket = useCallback(() => liveTicket(projectId, tester.id), [projectId, tester.id])
  const live = useLiveView(true, ticket, origin)
  const { view, problem } = live
  const now = useNow(1000)
  const [chosen, setChosen] = useState<string | null>(null)
  const install = pickDevice(view, chosen)
  const device = install ? view.devices[install] : undefined
  const status = liveStatus(view, problem, install)
  // Online: listed by the last status, or sending frames now (a live or paused session).
  const online =
    (view.online?.length ?? 0) > 0 ||
    view.order.some((i) => {
      const s = view.devices[i]?.state
      return s === 'live' || s === 'paused'
    })
  useEffect(() => {
    onOnline?.(online)
  }, [online, onOnline])
  useEffect(() => () => onOnline?.(false), [onOnline])

  // The filter: the Worker applies it, a moment after typing stops.
  const [text, setText] = useState('')
  const [level, setLevel] = useState<Level>('all')
  const { setFilter } = live
  useEffect(() => {
    const id = setTimeout(
      () =>
        setFilter({
          min_severity: level === 'all' ? 0 : SEVERITY_FLOOR[level],
          text: text.trim(),
        }),
      FILTER_DEBOUNCE_MS,
    )
    return () => clearTimeout(id)
  }, [text, level, setFilter])

  // Pause keeps the table as it was; the view goes on collecting underneath.
  const [frozen, setFrozen] = useState<LiveLine[] | null>(null)
  const lines = frozen ?? device?.lines ?? []
  const newer = frozen && device ? device.lines.indexOf(frozen[0] as LiveLine) : 0
  const waiting = frozen && device ? (newer === -1 ? device.lines.length : newer) : 0

  const devices = view.order.map((i) => view.devices[i] as LiveDevice)
  const onlineOf = (i: string) => view.online?.find((d) => d.install === i)

  return (
    <div className="pp-pane">
      <LiveBanner
        status={status}
        deadline={view.deadline}
        renewed={view.renewed?.allowed ?? false}
        renewing={live.renewing}
        renewProblem={live.renewProblem}
        renewRefused={live.renewRefused}
        wait={waitS(live.retryAt, now)}
        onRenew={() => live.renew()}
        onReconnect={live.reconnect}
        model={install ? onlineOf(install)?.model : undefined}
      />
      {devices.length > 1 ? (
        <label className="lbl pp-dev">
          {t('people.live.device')}
          <select
            className="select"
            value={install ?? ''}
            onChange={(e) => {
              setChosen(e.target.value)
              setFrozen(null)
            }}
            translate="no"
          >
            {devices.map((d) => {
              const o = onlineOf(d.install)
              return (
                <option key={d.install} value={d.install}>
                  {[o?.model, o?.os, shortId(d.install)].filter(Boolean).join(' · ')}
                </option>
              )
            })}
          </select>
        </label>
      ) : null}
      <div className="grid2">
        <div className="stack">
          <Section
            title={t('people.live.logs')}
            meta={t('people.live.logsMeta')}
            right={
              <Btn
                size="sm"
                aria-pressed={frozen !== null}
                disabled={frozen === null && status.kind !== 'live' && status.kind !== 'paused'}
                onClick={() => setFrozen(frozen ? null : (device?.lines ?? []))}
              >
                {frozen ? t('people.live.resume') : t('people.live.pause')}
              </Btn>
            }
          >
            <div className="pp-lf">
              <input
                className="input mono"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('people.live.filter')}
                aria-label={t('people.live.filterLabel')}
                autoComplete="off"
                spellCheck={false}
                maxLength={200}
              />
              <select
                className="select"
                value={level}
                onChange={(e) => setLevel(e.target.value as Level)}
                aria-label={t('people.live.level')}
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {t(`people.live.level.${l}`)}
                  </option>
                ))}
              </select>
              <span className="note" role="status">
                {[
                  t('people.live.count', { count: lines.length }),
                  device?.skipped ? t('people.live.skipped', { count: device.skipped }) : null,
                  frozen
                    ? t('people.live.pausedCount', { count: waiting })
                    : status.kind === 'live'
                      ? t('people.live.streaming')
                      : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            {lines.length === 0 ? (
              <EmptyNote title={t('people.live.noLines')}>
                {view.filter.text || view.filter.min_severity
                  ? t('people.live.noLinesFiltered')
                  : t('people.live.noLinesHint')}
              </EmptyNote>
            ) : (
              <Table label={t('people.live.logsTable')} className="pp-logs">
                <thead>
                  <tr>
                    <th className="f" scope="col">
                      {t('people.live.col.time')}
                    </th>
                    <th scope="col">{t('people.live.col.severity')}</th>
                    <th scope="col">{t('people.live.col.message')}</th>
                    <th className="last" scope="col">
                      {t('people.live.col.fields')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.slice(0, LINES_SHOWN).map((l) => (
                    <LogRow key={l.n} line={l} />
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
          <div className="grid-h">
            <MemoryChart device={device} />
            <BatteryChart device={device} />
          </div>
        </div>
        <div className="stack">
          <Section
            title={t('people.live.replay')}
            meta={
              device?.wireframeAt
                ? t('people.live.updated', {
                    ago: Math.max(0, Math.round((now - device.wireframeAt) / 1000)),
                  })
                : undefined
            }
          >
            <LiveWireframe wireframe={device?.wireframe} live={status.kind === 'live'} />
          </Section>
          <Commands
            projectId={projectId}
            testerId={tester.id}
            install={install}
            live={status.kind === 'live'}
            canEdit={canEdit}
            results={view.results}
            now={now}
          />
        </div>
      </div>
    </div>
  )
}

/** What the view is doing, and the one action that helps: renew or reconnect. */
function LiveBanner({
  status,
  deadline,
  renewed,
  renewing,
  renewProblem,
  renewRefused,
  wait,
  onRenew,
  onReconnect,
  model,
}: {
  status: LiveStatus
  deadline: number | null
  renewed: boolean
  renewing: boolean
  renewProblem: LiveProblem | null
  /** The Worker's words when it refused the renew. */
  renewRefused: string | null
  /** Seconds left of a 429's Retry-After: nothing is tried again before. */
  wait: number
  onRenew: () => void
  onReconnect: () => void
  model: string | undefined
}) {
  const i18n = useI18n()
  const { t } = i18n
  const until = deadline
    ? i18n.date(new Date(deadline).toISOString(), { timeStyle: 'short' })
    : null
  const renew = (
    <>
      {renewProblem ? (
        <span className="pp-warn">
          {renewProblem === 'rate_limited'
            ? t('people.live.problem.rate_limited')
            : renewRefused
              ? t('people.live.renewRefused', { reason: renewRefused })
              : t('people.live.renewFailed')}
        </span>
      ) : null}
      <Btn size="sm" onClick={onRenew} disabled={renewing || wait > 0}>
        {t('people.live.renew')}
      </Btn>
    </>
  )
  const reconnect = (
    <Btn size="sm" onClick={onReconnect} disabled={wait > 0}>
      {t('people.live.reconnect')}
    </Btn>
  )
  let tone = 'mute'
  let text: string
  let action: ReactNode = null
  switch (status.kind) {
    case 'problem':
      tone = 'warn'
      text = t(`people.live.problem.${status.problem}`)
      if (status.problem === 'failed' || status.problem === 'rate_limited') action = reconnect
      break
    case 'connecting':
      text = t('people.live.connecting')
      break
    case 'reconnecting':
      tone = 'warn'
      text = t('people.live.reconnecting')
      break
    case 'expired':
      text = t('people.live.expired')
      action = reconnect
      break
    case 'waiting':
      text = status.reason
        ? t(`people.live.waiting.${status.reason}`)
        : status.online
          ? t('people.live.waiting.starting', { model: model ?? '' })
          : t('people.live.waiting.none')
      break
    case 'live':
      tone = 'ok'
      text = until ? t('people.live.liveUntil', { until }) : t('people.live.live')
      action = renew
      break
    case 'paused':
      tone = 'warn'
      text = t(`people.live.paused.${status.reason ?? 'inactive'}`)
      break
    case 'ended':
      text = t(`people.live.ended.${status.reason ?? 'closed'}`)
      if (status.renew) action = renewed ? <span>{t('people.live.renewed')}</span> : renew
      break
  }
  return (
    <p className={`pp-live-st ${tone}`} role="status">
      <span>{text}</span>
      {action ? <span className="pp-r">{action}</span> : null}
    </p>
  )
}

function LogRow({ line }: { line: LiveLine }) {
  const i18n = useI18n()
  const level: SeverityName = severityName(line.severity)
  const fields = attributeText(line.attributes)
  return (
    <tr>
      <td className="f mono">
        {i18n.date(new Date(line.t).toISOString(), {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </td>
      <td>
        <span className={`sev ${level === 'trace' ? 'debug' : level}`} translate="no">
          {level}
        </span>
      </td>
      <td className="pp-msg" translate="no">
        {line.body}
      </td>
      <td className="last mono" translate="no">
        {fields}
      </td>
    </tr>
  )
}

const timeOf = (i18n: Translator, t: number) =>
  i18n.date(new Date(t).toISOString(), { hour: '2-digit', minute: '2-digit', second: '2-digit' })

function MemoryChart({ device }: { device: LiveDevice | undefined }) {
  const i18n = useI18n()
  const { t, number } = i18n
  const samples = (device?.samples ?? []).filter((s) => s.memory !== null)
  const mb = (bytes: number) => bytes / 1_048_576
  const lastMb = samples.length ? mb(samples.at(-1)?.memory ?? 0) : null
  return (
    <Section title={t('people.live.memory')} meta={t('people.live.memoryMeta')}>
      <div className="pp-chart">
        {samples.length < 2 || lastMb === null ? (
          <EmptyNote title={t('people.live.noSamples')} />
        ) : (
          <LineChart
            label={t('people.live.memoryLabel', { mb: number(Math.round(lastMb)) })}
            points={samples.map((s) => ({ label: timeOf(i18n, s.t), value: mb(s.memory ?? 0) }))}
            format={(v) => number(Math.round(v))}
            fromZero
            height={150}
          />
        )}
      </div>
    </Section>
  )
}

function BatteryChart({ device }: { device: LiveDevice | undefined }) {
  const i18n = useI18n()
  const { t, number } = i18n
  const samples = device?.samples ?? []
  const last = samples.at(-1)
  const levels = samples.filter((s) => s.battery !== null)
  const state = [
    last?.charging === true
      ? t('people.live.charging')
      : last?.charging === false
        ? t('people.live.notCharging')
        : null,
    last?.thermal ? t(`people.live.thermal.${last.thermal}`) : null,
    last?.lowPower ? t('people.live.lowPower') : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const pct = (v: number) => Math.round(v * 100)
  return (
    <Section title={t('people.live.battery')} meta={state || undefined}>
      <div className="pp-chart">
        {levels.length < 2 ? (
          <EmptyNote title={t('people.live.noBattery')}>{t('people.live.noBatteryHint')}</EmptyNote>
        ) : (
          <LineChart
            label={t('people.live.batteryLabel', { pct: number(pct(levels.at(-1)?.battery ?? 0)) })}
            points={levels.map((s) => ({ label: timeOf(i18n, s.t), value: pct(s.battery ?? 0) }))}
            format={(v) => number(Math.round(v))}
            unit="%"
            fromZero
            integer
            height={150}
          />
        )}
      </div>
    </Section>
  )
}

function LiveWireframe({ wireframe, live }: { wireframe: unknown; live: boolean }) {
  const { t } = useI18n()
  const screen = useMemo(() => (wireframe ? keyframeScreen(wireframe) : null), [wireframe])
  return (
    <div className="win pp-rp">
      <div className="win-bar">
        <b>{t('people.live.wireframe')}</b>
        {live ? <span className="pp-on">{t('people.live.onAir')}</span> : null}
      </div>
      {screen ? (
        <div className="pp-wf">
          <Wireframe screen={screen} label={t('people.live.wireframeLabel')} />
        </div>
      ) : (
        <p className="note">{t('people.live.noWireframe')}</p>
      )}
    </div>
  )
}

function Commands({
  projectId,
  testerId,
  install,
  live,
  canEdit,
  results,
  now,
}: {
  projectId: string
  testerId: string
  install: string | null
  live: boolean
  canEdit: boolean | undefined
  results: Record<string, CommandResult>
  now: number
}) {
  const { t } = useI18n()
  const command = useLiveCommand(projectId)
  const [armed, setArmed] = useState<CommandName | null>(null)
  // By device and command: what was sent to one device says nothing of another (the picker).
  const [sent, setSent] = useState<Partial<Record<string, Sent>>>({})
  const slot = (name: CommandName, on: string | null) => `${on ?? ''}|${name}`
  // The API's rate (10 commands a minute) holds every command until its Retry-After.
  const [retryAt, setRetryAt] = useState<number | null>(null)
  const wait = waitS(retryAt, now)

  function send(name: CommandName) {
    if (!install) return
    setArmed(null)
    const at = Date.now()
    setSent((s) => ({ ...s, [slot(name, install)]: { install, at } }))
    command.mutate(
      { tester_id: testerId, install, name },
      {
        onSuccess: (r) =>
          setSent((s) => ({
            ...s,
            [slot(name, install)]: { install, at, id: r.id, delivered: r.delivered },
          })),
        onError: (error) => {
          setRetryAt(waitUntil(error))
          setSent((s) => ({
            ...s,
            [slot(name, install)]: {
              install,
              at,
              error: isApiError(error, 409)
                ? 'not_live'
                : isApiError(error, 503)
                  ? 'unavailable'
                  : isApiError(error, 403)
                    ? 'forbidden'
                    : isApiError(error, 429)
                      ? 'rate_limited'
                      : 'failed',
            },
          }))
        },
      },
    )
  }

  const off =
    canEdit === false ? t('settings.adminOnly') : !live ? t('people.live.cmd.notLive') : null

  return (
    <Section title={t('people.live.commands')} meta={t('people.live.commandsMeta')}>
      <ul className="pp-cmds">
        {COMMAND_ROWS.map(({ name, title, body }) => {
          const s = sent[slot(name, install)]
          const result = s?.id ? results[s.id] : undefined
          const busy = Boolean(s && !s.error && !result && now - s.at < COMMAND_TIMEOUT_MS)
          return (
            <li key={name}>
              <span>
                <b>{t(title)}</b>
                <span className="pp-cd">{t(body)}</span>
                {s ? <SentLine sent={s} result={result} now={now} /> : null}
              </span>
              <span className="pp-cb">
                {armed === name ? (
                  <>
                    <Btn size="sm" variant="ghost" onClick={() => setArmed(null)}>
                      {t('people.pin.cancel')}
                    </Btn>
                    <Btn size="sm" variant="solid" autoFocus onClick={() => send(name)}>
                      {t('people.live.cmd.confirm')}
                    </Btn>
                  </>
                ) : (
                  <Btn
                    size="sm"
                    disabled={Boolean(off) || busy || !install || wait > 0}
                    title={off ?? undefined}
                    onClick={() => setArmed(name)}
                  >
                    {t('people.live.cmd.send')}
                  </Btn>
                )}
              </span>
              {result?.outcome === 'done' ? <ResultView result={result} /> : null}
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

function SentLine({
  sent,
  result,
  now,
}: {
  sent: Sent
  result: CommandResult | undefined
  now: number
}) {
  const i18n = useI18n()
  const { t } = i18n
  const at = timeOf(i18n, result?.at || sent.at)
  let text: string
  if (sent.error) text = t(`people.live.cmd.error.${sent.error}`)
  else if (!sent.id) text = t('people.live.cmd.sending')
  else if (result) text = t(`people.live.cmd.outcome.${result.outcome}`, { at })
  else if (now - sent.at >= COMMAND_TIMEOUT_MS) text = t('people.live.cmd.expired')
  else text = sent.delivered ? t('people.live.cmd.taken') : t('people.live.cmd.queued')
  return (
    <span className="pp-sent" role="status">
      {text}
    </span>
  )
}

function ResultView({ result }: { result: CommandResult }) {
  const i18n = useI18n()
  const { t } = i18n
  const r = result.result
  if (result.name === 'screenshot') {
    const wf = (r as { wireframe?: unknown } | null)?.wireframe ?? r
    return (
      <div className="pp-res">
        <LiveWireframe wireframe={wf} live={false} />
      </div>
    )
  }
  if (result.name === 'raise_log_level') {
    const until = Number((r as { until?: unknown } | null)?.until)
    return Number.isFinite(until) && until > 0 ? (
      <p className="pp-res note">
        {t('people.live.cmd.raisedUntil', {
          until: i18n.date(new Date(until).toISOString(), { timeStyle: 'short' }),
        })}
      </p>
    ) : null
  }
  const d = diagnostics(r)
  const mb = (bytes: number) => t('people.live.mb', { n: Math.round(bytes / 1_048_576) })
  // Each row: its label, its value, and whether the value is the device's own word (not translated).
  const rows: [MessageId, string | null, boolean?][] = [
    ['people.live.diag.memory', d.memory === null ? null : mb(d.memory)],
    ['people.live.diag.storage', d.storage === null ? null : mb(d.storage)],
    ['people.live.diag.thermal', d.thermal ? t(`people.live.thermalState.${d.thermal}`) : null],
    ['people.live.diag.network', d.network, true],
    [
      'people.live.diag.queue',
      d.queueRecords === null
        ? null
        : d.queueBytes === null
          ? t('people.live.diag.queueRecords', { records: d.queueRecords })
          : t('people.live.diag.queueValue', {
              records: d.queueRecords,
              mb: Math.round((d.queueBytes / 1_048_576) * 10) / 10,
            }),
    ],
  ]
  const shown = rows.filter(([, v]) => v !== null)
  return shown.length ? (
    <dl className="kv pp-kv pp-res">
      {shown.map(([k, v, raw]) => (
        <Fragment key={k}>
          <dt>{t(k)}</dt>
          <dd translate={raw ? 'no' : undefined}>{v}</dd>
        </Fragment>
      ))}
    </dl>
  ) : null
}
